import fontVariants from './font-variants.json';

/**
 * Shared font registry — the single source of truth for the bio renderer,
 * the style editor, and the RichTextEditor font picker. Loads
 * `font-variants.json` (1:1 with legacy `flows/bio/fonts.ts`) and exposes:
 *
 *  - lookup helpers (`findExportedFontByFamily` / `…ByAlias`) used to resolve a
 *    creator-picked family string to its real `fontFamily` + `fontWeight` +
 *    `fontStyle` (e.g. `Baskervville Italic` → family `Baskervville`, italic).
 *  - Google-Fonts CSS2 URL builders (`buildGoogleFontsUrl`, `formatAxisValue`).
 *  - `resolveFontFace` / `googleFontHrefForFamily` for the editor's
 *    apply-and-preload path.
 *
 * Moved here from `libs/features/bio` so foundation packages (`libs/ui`'s
 * RichTextEditor) can share it without importing upward into a feature slice.
 * The old bio paths re-export from here so existing consumers are unchanged.
 */

export type FontVariantOption = string;

export type ExportedFontFamily = {
  fontId: string;
  baseFontFamily: string;
  fontFamily: string;
  fontFamilyAlias?: string;
  fontWeight:
    | '100'
    | '200'
    | '300'
    | '400'
    | '500'
    | '600'
    | '700'
    | '800'
    | '900';
  fontStyle: 'normal' | 'italic';
};

export const kExportedFonts = fontVariants as {
  variants: {
    body?: ExportedFontFamily[];
    heading?: ExportedFontFamily[];
    button?: ExportedFontFamily[];
    default?: ExportedFontFamily[];
  };
};

export const kPredefinedFontList =
  kExportedFonts.variants.default ??
  kExportedFonts.variants.body ??
  kExportedFonts.variants.heading ??
  kExportedFonts.variants.button ??
  [];

const kAllFontList = new Map<string, ExportedFontFamily>();
const kExportedFontAliasMap = new Map<string, ExportedFontFamily>();
// Reverse map: BASE family name → its variant record. Some fonts expose ONLY a
// weighted variant (e.g. base "Lexend" has a single registry entry, "Lexend
// Semi Bold" @600 — there is no plain "Lexend" record). The renderer emits the
// BASE family as CSS `font-family`, but the `font_config` scale record is keyed
// by the VARIANT name, so a base→variant bridge is needed to find the per-font
// location scales. Prefer the NORMAL (non-italic) variant when a base has more
// than one (e.g. Canela normal + italic) — the italic is a separate style the
// caller already carries.
const kExportedFontByBaseMap = new Map<string, ExportedFontFamily>();

const addFonts = (fonts: ExportedFontFamily[]) => {
  fonts.forEach(font => {
    if (!kAllFontList.has(font.fontFamily)) {
      kAllFontList.set(font.fontFamily, font);
      if (font.fontFamilyAlias) {
        kExportedFontAliasMap.set(font.fontFamilyAlias, font);
      }
    }
    const existingByBase = kExportedFontByBaseMap.get(font.baseFontFamily);
    // Keep the first normal variant; only overwrite if we haven't stored a
    // normal one yet and this is normal (italic never displaces a normal).
    if (
      existingByBase == null ||
      (existingByBase.fontStyle === 'italic' && font.fontStyle !== 'italic')
    ) {
      kExportedFontByBaseMap.set(font.baseFontFamily, font);
    }
  });
};

addFonts(kExportedFonts.variants.body ?? []);
addFonts(kExportedFonts.variants.heading ?? []);
addFonts(kExportedFonts.variants.button ?? []);
addFonts(kExportedFonts.variants.default ?? []);

export const getAllExportedFonts = () => kAllFontList.values();

export const findExportedFontByFamily = (fontFamily: string) => {
  return kAllFontList.get(fontFamily);
};

export const findExportedFontByAlias = (fontFamilyAlias: string) => {
  return kExportedFontAliasMap.get(fontFamilyAlias);
};

/**
 * Resolve a BASE family name (the CSS `font-family` the renderer emits, e.g.
 * "Lexend") back to its registry variant record (e.g. "Lexend Semi Bold"), so
 * callers can look up the variant-keyed `font_config` scales. Prefers the
 * normal (non-italic) variant. Returns undefined for an unknown base.
 */
export const findExportedFontByBaseFamily = (baseFontFamily: string) => {
  return kExportedFontByBaseMap.get(baseFontFamily);
};

export const findExportedFont = (
  predict: (fontConfig: ExportedFontFamily) => boolean,
) => {
  for (const font of kAllFontList.values()) {
    if (predict(font)) {
      return font;
    }
  }
  return undefined;
};

// ─── Google Fonts URL builders ─────────────────────────────────────────────

/**
 * Build the `wght` or `ital,wght` axis value for a single registry variant:
 *   - `{ 400, normal }` → `400`
 *   - `{ 400, italic }` → `1,400`
 */
export function formatAxisValue(variant: ExportedFontFamily): string {
  const italic = variant.fontStyle === 'italic';
  const weight = variant.fontWeight;
  return italic ? `1,${weight}` : weight;
}

/**
 * Compose the final Google Fonts CSS2 URL. Uses the `ital,wght` tuple form
 * when any axis is italic, otherwise plain `wght@`.
 */
export function buildGoogleFontsUrl(family: string, axes: string[]): string {
  const hasItalic = axes.some(a => a.includes(','));

  let axisParam: string;
  if (hasItalic) {
    const tuples = axes.map(a => (a.includes(',') ? a : `0,${a}`)).sort();
    axisParam = `ital,wght@${tuples.join(';')}`;
  } else {
    const sorted = Array.from(new Set(axes)).sort();
    axisParam = `wght@${sorted.join(';')}`;
  }

  return `https://fonts.googleapis.com/css2?family=${family}:${axisParam}&display=swap`;
}

/**
 * Strip variant / weight tokens legacy folds into the family string
 * (e.g. `Lexend Semi Bold` → `Lexend`, `Playfair Display Italic` →
 * `Playfair Display`).
 */
export function stripFontVariant(family: string): string {
  const VARIANT_SUFFIXES = [
    'Variable',
    'Extra Light',
    'Semi Bold',
    'Extra Bold',
    'Bold',
    'Medium',
    'Light',
    'Thin',
    'Italic',
    'Regular',
  ];
  let clean = family.trim();
  let changed = true;
  while (changed) {
    changed = false;
    for (const suffix of VARIANT_SUFFIXES) {
      const re = new RegExp(`\\s+${suffix}$`);
      if (re.test(clean)) {
        clean = clean.replace(re, '');
        changed = true;
      }
    }
  }
  return clean;
}

// ─── Editor apply + preload helpers ────────────────────────────────────────

/** Self-hosted faces (not on Google Fonts) — skip emitting `<link>`s. */
const kSelfHostedFonts = new Set(['TAN Pearl', 'Horizon']);

/**
 * The font list every editor PICKER should offer — the full predefined list
 * minus self-hosted faces that aren't bundled on the Next side yet (they'd
 * silently fall back to a generic sans on apply, so listing them is worse than
 * hiding them). The renderer still resolves self-hosted names for values a
 * creator saved through an older flow via `findExportedFontByFamily`; this only
 * governs what the pickers expose. Single source of truth so the profile-name /
 * bio picker and the style-editor font dropdown offer an identical set.
 */
export const kSelectableFontList = kPredefinedFontList.filter(
  font => !kSelfHostedFonts.has(font.baseFontFamily),
);

export interface ResolvedFontFace {
  /** The actual CSS `font-family` the browser must render (the base face). */
  fontFamily: string;
  fontWeight: string;
  fontStyle: 'normal' | 'italic';
}

/**
 * Resolve a picked family (alias like `DM Sans Semi Bold`, or a base family)
 * to the real face the browser can render: base `fontFamily` + `fontWeight` +
 * `fontStyle`. Google serves the base family at a weight, not a face literally
 * named "DM Sans Semi Bold" — so applying the alias verbatim never renders.
 * Falls back to the stripped base name at weight 400 for unregistered families.
 */
export function resolveFontFace(family: string): ResolvedFontFace {
  const variant =
    findExportedFontByFamily(family) ?? findExportedFontByAlias(family);
  if (variant) {
    return {
      fontFamily: variant.baseFontFamily,
      fontWeight: variant.fontWeight,
      fontStyle: variant.fontStyle,
    };
  }
  return {
    fontFamily: stripFontVariant(family),
    fontWeight: '400',
    fontStyle: 'normal',
  };
}

/**
 * Google Fonts `<link href>` for a single picked family, or `null` when the
 * face is self-hosted / unknown (caller lets CSS fall back). Used by the editor
 * to preload the faces its dropdown can apply.
 */
export function googleFontHrefForFamily(family: string): string | null {
  const variant =
    findExportedFontByFamily(family) ?? findExportedFontByAlias(family);
  if (!variant) return null;
  if (kSelfHostedFonts.has(variant.baseFontFamily)) return null;
  return buildGoogleFontsUrl(variant.baseFontFamily, [
    formatAxisValue(variant),
  ]);
}
